import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import useContactMatches from "../../data/useContactMatches";
import { IContactMatch } from "../../models/Contact";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { matchReason } from "../../utils/contactDuplicates";

interface PossibleContactMatchesProps {
  email: string | null | undefined;
  phone: string | null | undefined;
  // Editing an existing contact: leave it out of its own matches.
  excludeContactId?: string;
  // When given, each match offers "Use this contact" instead of creating a new one.
  onUse?: (match: IContactMatch) => void;
}

// Shown while a new contact is being typed in, when someone with the same email or mobile already
// exists. Creating a second row for the same person splits their history and, at invoice time, gives
// them a second account - this is the moment to stop it.
export default function PossibleContactMatches({ email, phone, excludeContactId, onUse }: PossibleContactMatchesProps) {
  const { t } = useTranslation("accounts");
  const matches = useContactMatches(email, phone, excludeContactId, "PossibleContactMatches");
  const rows = matches.data?.data ?? [];

  if (rows.length === 0) return null;

  const matchedOn = (match: IContactMatch) => t(matchReason(match));

  return (
    <div className="col-span-full rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-amber-900">{t("This looks like someone you already have")}</p>
          <p className="text-amber-800">
            {onUse
              ? t("Use the existing contact rather than creating a duplicate, unless this really is a different person.")
              : t("Another contact already has this email or mobile. Check it is not the same person.")}
          </p>
          <ul className="mt-2 divide-y divide-amber-200">
            {rows.map((match) => (
              <li key={match.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900">
                    {match.fullname}
                    {match.account && (
                      <span className="font-normal text-gray-600">
                        {" "}
                        - {t("Account")} #{match.account.accountNumber} {match.account.name}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-gray-600">
                    {[match.email, match.telephone1, match.telephone2, match.postcode].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-amber-800">
                    {matchedOn(match)} · {t("Leads: {{count}}", { count: match.leadCount })}
                  </p>
                </div>
                {onUse && (
                  <ButtonInput
                    label={t("Use this contact")}
                    isSubmit={false}
                    onClick={() => onUse(match)}
                    classes="shrink-0"
                    size="sm"
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
